"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";


const benefits = [
{
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>,

  title: "Patient Retention",
  desc: "Patients who can fund their treatment are 3x more likely to complete full cycles."
},
{
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
      </svg>,

  title: "White-Label Portal",
  desc: "Embed BabyBank directly into your patient portal with your clinic\'s branding."
},
{
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>,

  title: "Treatment Milestones",
  desc: "Auto-sync campaign milestones with treatment plans — retrieval, transfer, and beyond."
},
{
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>,

  title: "HIPAA-Compliant",
  desc: "Patient data stays protected. We never share medical information with donors."
}];


export default function ForClinicsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add("visible");});},
      { threshold: 0.12 }
    );
    sectionRef?.current?.querySelectorAll(".reveal, .reveal-right")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="for-clinics" className="py-24 md:py-32 relative overflow-hidden" ref={sectionRef}
    style={{ background: "#FFFFFF" }}>
      {/* Decorative blob */}
      <div className="absolute right-0 top-0 w-[500px] h-[500px] pointer-events-none"
      style={{ background: "radial-gradient(ellipse, rgba(90, 138, 111, 0.08) 0%, transparent 70%)", transform: "translate(20%, -20%)" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Image + floating UI */}
          <div className="relative order-2 lg:order-1">
            <div className="reveal-right relative rounded-4xl overflow-hidden shadow-warm-lg"
            style={{ aspectRatio: "4/3" }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_16a8790aa-1772160072128.png"
                alt="Fertility clinic consultation room with warm lighting and caring staff"
                fill
                className="object-cover" />
              
              <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(90, 138, 111, 0.15) 0%, transparent 60%)" }} />
            </div>

            {/* Floating stat card */}
            <div className="reveal absolute -bottom-6 -right-4 lg:-right-8 glass-card rounded-2xl p-5 shadow-glass max-w-[220px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #5A8A6F, #3D6B52)" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-ink-muted font-medium">Avg. completion rate</p>
                  <p className="text-xl font-heading font-bold text-growth">+47%</p>
                </div>
              </div>
              <p className="text-xs text-ink-muted leading-relaxed">Clinics using BabyBank see higher treatment completion.</p>
            </div>
          </div>

          {/* Right: Content */}
          <div className="order-1 lg:order-2 space-y-8">
            <div className="reveal space-y-4">
              <span className="section-tag-green">For Fertility Clinics</span>
              <h2 className="font-heading font-bold text-ink" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                Offer BabyBank to your patients —{" "}
                <span className="text-gradient-primary">enhance support without extra work</span>
              </h2>
              <p className="text-lg text-ink-muted leading-relaxed">
                Help your patients remove the financial barrier to treatment. BabyBank integrates seamlessly into your workflow and gives patients a compassionate, private way to get community support.
              </p>
            </div>

            {/* Benefits grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {benefits?.map((b, i) =>
              <div key={b?.title}
              className="reveal p-5 rounded-2xl border border-warm-border-light hover:border-growth/30 hover:shadow-warm-sm transition-all duration-300 group"
              style={{ background: "#FAFAF8", animationDelay: `${i * 0.1}s` }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300"
                style={{ background: "#D4EAD9", color: "#3D6B52" }}>
                    {b?.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-ink mb-1">{b?.title}</h3>
                  <p className="text-xs text-ink-muted leading-relaxed">{b?.desc}</p>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="reveal flex flex-col sm:flex-row gap-3">
              <a href="#contact" className="btn-growth text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Request Partnership Demo
              </a>
              <button className="btn-secondary text-sm">
                Download Clinic Overview
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

}